import { request } from '@/lib/api'
import type {
  ApprovalQueue,
  ApprovalRequest,
  Delegation,
  Delegations,
  MyTasks,
  Task,
  Timeline,
} from './types'

/**
 * Клиент API согласований, задач и ленты (тикет #79).
 *
 * Ни один запрос не передаёт «чьё»: очередь, делегирования и задачи сервер отдаёт по
 * учётной записи вызывающего. Параметр здесь означал бы, что чужую очередь можно
 * прочитать, подставив идентификатор в адресную строку.
 */

// ------------------------------------------------------------------ согласования

/** Что ждёт подписи текущего пользователя — своё и делегированное. */
export function fetchApprovalQueue(signal?: AbortSignal): Promise<ApprovalQueue> {
  return request<ApprovalQueue>('/approvals/my', { signal })
}

/**
 * История согласований документа.
 *
 * Все запросы, а не последний: отказ, предшествовавший согласию, — часть записи, и без
 * него карточка отвечает «согласовано», умалчивая, с какого раза.
 */
export function fetchApprovalHistory(
  entityType: string,
  entityId: string,
  signal?: AbortSignal,
): Promise<ApprovalRequest[]> {
  return request<ApprovalRequest[]>(`/approvals/history/${entityType}/${entityId}`, { signal })
}

export function approveRequest(requestId: string, comment: string): Promise<ApprovalRequest> {
  return request<ApprovalRequest>(`/approvals/${requestId}/approve`, {
    method: 'POST',
    body: { comment },
  })
}

/**
 * Отклонить.
 *
 * Комментарий обязателен, и проверяет это сервер: отказ без причины нельзя отработать —
 * тому, кто просил, неизвестно, что менять перед повторной попыткой.
 */
export function rejectRequest(requestId: string, comment: string): Promise<ApprovalRequest> {
  return request<ApprovalRequest>(`/approvals/${requestId}/reject`, {
    method: 'POST',
    body: { comment },
  })
}

/** Отправить документ на согласование; `required=false` — порог не перейдён. */
export function submitForApproval(
  entityType: string,
  entityId: string,
): Promise<{ required: boolean; request: ApprovalRequest | null }> {
  return request<{ required: boolean; request: ApprovalRequest | null }>(
    `/approvals/submit/${entityType}/${entityId}`,
    { method: 'POST' },
  )
}

// ------------------------------------------------------------------ делегирование

export function fetchDelegations(signal?: AbortSignal): Promise<Delegations> {
  return request<Delegations>('/approvals/delegations', { signal })
}

export function createDelegation(input: {
  delegateId: string
  startsOn?: string | null
  endsOn?: string | null
  reason?: string | null
}): Promise<Delegation> {
  return request<Delegation>('/approvals/delegations', {
    method: 'POST',
    body: {
      delegateId: input.delegateId,
      startsOn: input.startsOn ?? null,
      endsOn: input.endsOn ?? null,
      reason: input.reason ?? null,
    },
  })
}

/** Отозвать; строка остаётся с отметкой — это журнал. */
export function revokeDelegation(delegationId: string): Promise<Delegation> {
  return request<Delegation>(`/approvals/delegations/${delegationId}`, { method: 'DELETE' })
}

// ------------------------------------------------------------------ лента и задачи

export function fetchTimeline(
  entityType: string,
  entityId: string,
  limit = 50,
  signal?: AbortSignal,
  /** `nextCursor` предыдущей страницы (#114); непрозрачен — только пересылается. */
  cursor?: string | null,
): Promise<Timeline> {
  const after = cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
  return request<Timeline>(`/timeline/${entityType}/${entityId}?limit=${limit}${after}`, { signal })
}

export function fetchMyTasks(signal?: AbortSignal): Promise<MyTasks> {
  return request<MyTasks>('/tasks/my', { signal })
}

export function completeTask(activityId: string, outcome?: string): Promise<Task> {
  return request<Task>(`/tasks/${activityId}/complete`, {
    method: 'POST',
    body: { outcome: outcome ?? null },
  })
}
